import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HiArrowLeft } from 'react-icons/hi2';
import { useNavigate, useParams } from 'react-router-dom';
import { useTVConfiguration } from '../lib/contexts/TVConfigurationContext';
import { useUserData } from '../lib/contexts/UserContext';
import { useHospDocData } from '../lib/contexts/HospitalDoctorContext';
import { Logo } from '../assets/icons/Icons';
import { TVConfiguration } from '../lib/apis/tvConfig';
import { toast } from 'react-toastify';
import { 
  getProfilesByHospitalId, 
  getProfileByHospitalAndProfileId,
  updateProfileByHospitalAndProfileId,
  Profile 
} from '../lib/apis/profile';
import { getDoctorListByHospitalId } from '../lib/apis/doctor';
import { Doctor } from '../lib/utils/types';
import { setAudioLanguageToLocalStorage } from '../lib/utils/speechUtils';

type ProfileSettings = {
  view: 'column' | 'carousel' | 'single-doctor';
  language: string;
  selectedDoctors: string[];
  selectedViewTypes: string[];
};



const UserIcon = ({ color }: { color: string }) => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path
      d="M16 16C18.7614 16 21 13.7614 21 11C21 8.23858 18.7614 6 16 6C13.2386 6 11 8.23858 11 11C11 13.7614 13.2386 16 16 16Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 26C7 22.134 10.134 19 14 19H18C21.866 19 25 22.134 25 26"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SettingsIcon = ({ color }: { color: string }) => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <path
      d="M10.325 4.317a1.65 1.65 0 013.35 0l.116.996a1.651 1.651 0 002.458 1.304l.88-.51a1.65 1.65 0 012.25.6l1.65 2.86a1.65 1.65 0 01-.6 2.25l-.88.51a1.651 1.651 0 000 2.862l.88.51a1.65 1.65 0 01.6 2.25l-1.65 2.86a1.65 1.65 0 01-2.25.6l-.88-.51a1.651 1.651 0 00-2.458 1.304l-.116.996a1.65 1.65 0 01-3.35 0l-.116-.996a1.651 1.651 0 00-2.458-1.304l-.88.51a1.65 1.65 0 01-2.25-.6l-1.65-2.86a1.65 1.65 0 01.6-2.25l.88-.51a1.651 1.651 0 000-2.862l-.88-.51a1.65 1.65 0 01-.6-2.25l1.65-2.86a1.65 1.65 0 012.25-.6l.88.51a1.651 1.651 0 002.458-1.304l.116-.996z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { hospital_id } = useParams<{ hospital_id: string }>();
  const { accessToken } = useUserData();
  const { setDoctors, allDoctorsData } = useHospDocData();
  const { 
    tvConfig, 
    loading, 
    saving, 
    error, 
    loadTVConfiguration, 
    updateConfiguration, 
    clearError 
  } = useTVConfiguration();

  const [localConfig, setLocalConfig] = useState<TVConfiguration | null>(null);
  const hasLoaded = useRef(false);
  const isLoadingProfile = useRef(false);
  const isSavingProfile = useRef(false);
  const lastSavedProfileSettings = useRef<string>('');
  const userModifiedTVSettings = useRef(false);
  const [currentProfile, setCurrentProfile] = useState<string>('');
  const [currentProfileData, setCurrentProfileData] = useState<Profile | null>(null);
  const [profileSettings, setProfileSettings] = useState<ProfileSettings>({
    view: 'column',
    language: 'en',
    selectedDoctors: [],
    selectedViewTypes: ['column'],
  });

  const viewOptions = [
    { value: 'column', label: 'Column' },
    { value: 'carousel', label: 'Carousel' },
    { value: 'single-doctor', label: 'Single Doctor' },
  ];
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'kn', label: 'Kannada' },
    { value: 'ta', label: 'Tamil' },
    { value: 'hi', label: 'Hindi' },
    { value: 'ar', label: 'Arabic' },
    { value: 'te', label: 'Telugu' },
    { value: 'mr', label: 'Marathi' },
  ];
  const [doctorOptions, setDoctorOptions] = useState<Doctor[]>([]);
  const [apiProfiles, setApiProfiles] = useState<Profile[]>([]);
  const [defaultProfileId, setDefaultProfileId] = useState<string | null>(null);

  const getSavedProfileId = (hospitalId: string): string | null => {
    try {
      const saved = localStorage.getItem(`selectedProfile_${hospitalId}`);
      return saved || null;
    } catch {
      return null;
    }
  };

  const saveProfileId = (hospitalId: string, profileId: string) => {
    try {
      localStorage.setItem(`selectedProfile_${hospitalId}`, profileId);
    } catch (error) {
      console.error("Error saving profile to localStorage:", error);
    }
  };

  const loadProfiles = useCallback(async (hospitalId: string) => {
    if (!accessToken) return;

    try {
      const response = await getProfilesByHospitalId(hospitalId);
      
      if (response?.status === 200 && (response?.data?.status === "SUCCESS" || response?.data?.status === 1)) {
        const profiles = (response.data.result || response.data.data || []).filter((p: any) => p.is_active !== false);
        
        if (profiles.length > 0) {
          setApiProfiles(profiles);

          const defaultProfile = profiles.find((p: any) => p.is_default === true);
          
          if (defaultProfile) {
            const defaultId = defaultProfile.id || defaultProfile.tv_config_profile_id || defaultProfile.profile_id;
            setDefaultProfileId(defaultId);
          } else {
            setDefaultProfileId(null);
          }

          // Always check for saved profile first, prioritize it over current state
          const savedProfileId = getSavedProfileId(hospitalId);
          const savedProfile = savedProfileId ? profiles.find((p: any) => {
            const pId = p.id || p.tv_config_profile_id || p.profile_id;
            return pId === savedProfileId;
          }) : null;

          if (savedProfile) {
            const savedId = savedProfile.id || savedProfile.tv_config_profile_id || savedProfile.profile_id;
            if (savedId && savedId !== currentProfile) {
              setCurrentProfile(savedId);
            }
          } else if (!currentProfile) {
            // Only set default/first profile if no saved profile and no current profile
            if (defaultProfile) {
              const defaultId = defaultProfile.id || defaultProfile.tv_config_profile_id || defaultProfile.profile_id;
              setCurrentProfile(defaultId);
            } else if (profiles.length > 0) {
              const firstProfileId = profiles[0].id || profiles[0].tv_config_profile_id || profiles[0].profile_id;
              setCurrentProfile(firstProfileId);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error loading profiles:", error);
    }
  }, [accessToken, currentProfile]);

  useEffect(() => {
    if (hospital_id && accessToken && !hasLoaded.current) {
      hasLoaded.current = true;
      loadTVConfiguration(hospital_id);
      loadProfiles(hospital_id);
    }
  }, [hospital_id, loadTVConfiguration, accessToken, loadProfiles]);

  useEffect(() => {
    const loadDoctors = async () => {
      if (!hospital_id || !accessToken) return;

      try {
        const response = await getDoctorListByHospitalId(hospital_id);
        
        if (response?.status === 200 && response?.data?.status === "SUCCESS") {
          const doctors = response.data.result || [];
          setDoctorOptions(doctors.filter((doc: any) => doc && !doc.hide_from_tv));
        }
      } catch (error) {
        console.error("Error loading doctors:", error);
      }
    };

    loadDoctors();
  }, [hospital_id, accessToken]);

  useEffect(() => {
    if (tvConfig && !currentProfileData) {
      setLocalConfig(tvConfig);
    }
  }, [tvConfig, currentProfileData]);

  useEffect(() => {
    if (currentProfileData && localConfig && !isLoadingProfile.current && !userModifiedTVSettings.current) {
      const needsUpdate = 
        (currentProfileData.token !== undefined && Boolean(currentProfileData.token) !== localConfig.token) ||
        (currentProfileData.patient_name !== undefined && Boolean(currentProfileData.patient_name) !== localConfig.patient_name) ||
        (currentProfileData.room_number !== undefined && Boolean(currentProfileData.room_number) !== localConfig.room_number) ||
        (currentProfileData.slot !== undefined && Boolean(currentProfileData.slot) !== localConfig.slot) ||
        (currentProfileData.booking_mode !== undefined && Boolean(currentProfileData.booking_mode) !== localConfig.booking_mode);
      
      if (needsUpdate) {
        setLocalConfig(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            token: currentProfileData.token !== undefined ? Boolean(currentProfileData.token) : (prev.token ?? true),
            patient_name: currentProfileData.patient_name !== undefined ? Boolean(currentProfileData.patient_name) : (prev.patient_name ?? true),
            room_number: currentProfileData.room_number !== undefined ? Boolean(currentProfileData.room_number) : (prev.room_number ?? false),
            slot: currentProfileData.slot !== undefined ? Boolean(currentProfileData.slot) : (prev.slot ?? false),
            booking_mode: currentProfileData.booking_mode !== undefined ? Boolean(currentProfileData.booking_mode) : (prev.booking_mode ?? true),
          };
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProfileData]);

  useEffect(() => {
    hasLoaded.current = false;
  }, [hospital_id]);

  useEffect(() => {
    if (apiProfiles.length > 0 && hospital_id) {
      // Always check for saved profile first, prioritize it
      const savedProfileId = getSavedProfileId(hospital_id);
      const savedProfile = savedProfileId ? apiProfiles.find((p: any) => {
        const pId = p.id || p.tv_config_profile_id || p.profile_id;
        return pId === savedProfileId;
      }) : null;

      if (savedProfile) {
        const savedId = savedProfile.id || savedProfile.tv_config_profile_id || savedProfile.profile_id;
        if (savedId && savedId !== currentProfile) {
          setCurrentProfile(savedId);
        }
      } else if (!currentProfile) {
        // Only set default/first profile if no saved profile and no current profile
        if (defaultProfileId) {
          setCurrentProfile(defaultProfileId);
        } else if (apiProfiles.length > 0) {
          const firstProfileId = apiProfiles[0].id || apiProfiles[0].tv_config_profile_id || apiProfiles[0].profile_id;
          if (firstProfileId) {
            setCurrentProfile(firstProfileId);
          }
        }
      }
    }
  }, [apiProfiles, defaultProfileId, currentProfile, hospital_id]);
  useEffect(() => {
    const loadProfileSettings = async () => {
      if (!hospital_id || !currentProfile || !accessToken || isLoadingProfile.current) return;

      isLoadingProfile.current = true;

      const extractDoctorIds = (doctorsList: any[]): string[] => {
        return doctorsList.map((doc: any) => {
          if (typeof doc === 'object' && doc !== null) {
            return doc.id || doc.doctor_id || doc.mapping_id || null;
          }
          return typeof doc === 'string' ? doc.trim() : null;
        }).filter(Boolean).map((id: string) => String(id).trim());
      };

      const fetchDoctorsIfNeeded = async (): Promise<Doctor[]> => {
        if (doctorOptions && doctorOptions.length > 0) {
          return doctorOptions;
        }
        const docResponse = await getDoctorListByHospitalId(hospital_id);
        if (docResponse?.status === 200 && docResponse?.data?.status === "SUCCESS") {
          const fetchedDoctors = docResponse.data.result || [];
          const filtered = fetchedDoctors.filter((doc: any) => doc && !doc.hide_from_tv);
          setDoctorOptions(filtered);
          return filtered;
        }
        return [];
      };

      try {
        const response = await getProfileByHospitalAndProfileId(hospital_id, currentProfile);
        
        if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
          const profileData = response.data.data || response.data.result || response.data;
          
          if (profileData) {
            setCurrentProfileData(profileData);
            
            let selectedDoctorIds: string[] = [];
            if (profileData.doctors_list && Array.isArray(profileData.doctors_list) && profileData.doctors_list.length > 0) {
              await fetchDoctorsIfNeeded();
              selectedDoctorIds = extractDoctorIds(profileData.doctors_list);
            }
            
            setProfileSettings({
              view: (profileData.view_type || 'column') as 'column' | 'carousel' | 'single-doctor',
              language: (profileData.language || 'en') as string,
              selectedDoctors: selectedDoctorIds,
              selectedViewTypes: profileData.view_type ? [profileData.view_type] : ['column'],
            });

            userModifiedTVSettings.current = false;
            
            const baseConfig = localConfig || tvConfig;
            if (baseConfig) {
              setLocalConfig({
                ...baseConfig,
                token: profileData.token !== undefined ? Boolean(profileData.token) : (baseConfig.token ?? true),
                patient_name: profileData.patient_name !== undefined ? Boolean(profileData.patient_name) : (baseConfig.patient_name ?? true),
                room_number: profileData.room_number !== undefined ? Boolean(profileData.room_number) : (baseConfig.room_number ?? false),
                slot: profileData.slot !== undefined ? Boolean(profileData.slot) : (baseConfig.slot ?? false),
                booking_mode: profileData.booking_mode !== undefined ? Boolean(profileData.booking_mode) : (baseConfig.booking_mode ?? true),
              });
            } else if (hospital_id) {
              const defaultConfig = tvConfig || {
                tv_config_id: '',
                hospital_id: hospital_id,
                token: true,
                patient_name: true,
                room_number: false,
                slot: false,
                booking_mode: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                hospital_name: profileData.hospital_name || '',
              };
              
              setLocalConfig({
                ...defaultConfig,
                token: profileData.token !== undefined ? Boolean(profileData.token) : defaultConfig.token,
                patient_name: profileData.patient_name !== undefined ? Boolean(profileData.patient_name) : defaultConfig.patient_name,
                room_number: profileData.room_number !== undefined ? Boolean(profileData.room_number) : defaultConfig.room_number,
                slot: profileData.slot !== undefined ? Boolean(profileData.slot) : defaultConfig.slot,
                booking_mode: profileData.booking_mode !== undefined ? Boolean(profileData.booking_mode) : defaultConfig.booking_mode,
              });
            }
          } else {
            toast.error("Failed to load profile from API");
          }
        } else {
          toast.error("Failed to load profile from API");
        }
      } catch (error) {
        console.error("Error loading profile settings:", error);
        toast.error("Error loading profile settings");
      } finally {
        isLoadingProfile.current = false;
      }
    };

    loadProfileSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProfile, hospital_id, accessToken, defaultProfileId]);
  useEffect(() => {
    if (!hospital_id || !currentProfile || !accessToken || isSavingProfile.current || isLoadingProfile.current || !localConfig) return;

    const profileSettingsKey = JSON.stringify({
      language: profileSettings.language,
      viewType: profileSettings.selectedViewTypes[0] || profileSettings.view,
      doctors: profileSettings.selectedDoctors.sort().join(','),
      token: localConfig.token,
      patient_name: localConfig.patient_name,
      room_number: localConfig.room_number,
      slot: localConfig.slot,
      booking_mode: localConfig.booking_mode,
    });

    if (profileSettingsKey === lastSavedProfileSettings.current) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      if (isSavingProfile.current || isLoadingProfile.current) return;
      
      isSavingProfile.current = true;
      try {
        if (currentProfile === defaultProfileId) {
          const response = await updateProfileByHospitalAndProfileId(
            hospital_id,
            currentProfile,
            {
              language: profileSettings.language as "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr" | null,
              token: localConfig.token,
              patient_name: localConfig.patient_name,
              room_number: localConfig.room_number,
              slot: localConfig.slot,
              booking_mode: localConfig.booking_mode,
            }
          );

          if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
            lastSavedProfileSettings.current = profileSettingsKey;
            await updateConfiguration(hospital_id, {
              token: localConfig.token,
              patient_name: localConfig.patient_name,
              room_number: localConfig.room_number,
              slot: localConfig.slot,
              booking_mode: localConfig.booking_mode
            });
          }
        } else {
          const primaryViewType = profileSettings.selectedViewTypes[0] || profileSettings.view;
          
          const response = await updateProfileByHospitalAndProfileId(
            hospital_id,
            currentProfile,
            {
              view_type: primaryViewType as "column" | "carousel" | "single-doctor" | null,
              language: profileSettings.language as "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr" | null,
              doctors_list: profileSettings.selectedDoctors,
              token: localConfig.token,
              patient_name: localConfig.patient_name,
              room_number: localConfig.room_number,
              slot: localConfig.slot,
              booking_mode: localConfig.booking_mode,
            }
          );

          if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
            lastSavedProfileSettings.current = profileSettingsKey;
            await updateConfiguration(hospital_id, {
              token: localConfig.token,
              patient_name: localConfig.patient_name,
              room_number: localConfig.room_number,
              slot: localConfig.slot,
              booking_mode: localConfig.booking_mode
            });
          }
        }
      } catch (error) {
        console.error("Error saving profile settings:", error);
      } finally {
        isSavingProfile.current = false;
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [currentProfile, profileSettings, hospital_id, accessToken, defaultProfileId, localConfig, updateConfiguration]);

  const handleProfileChange = (profileId: string) => {
    if (profileId === currentProfile) return;
    lastSavedProfileSettings.current = '';
    userModifiedTVSettings.current = false;
    setCurrentProfile(profileId);
    if (hospital_id) {
      saveProfileId(hospital_id, profileId);
    }
    const apiProfile = apiProfiles.find((p: any) => {
      const pId = p.id || p.tv_config_profile_id || p.profile_id;
      return pId === profileId;
    });
    const profileName = apiProfile 
      ? (apiProfile.is_default ? "Default Profile" : (apiProfile.tv_profile_name || apiProfile.profile_name))
      : profileId;
    toast.info(`Switched to ${profileName}`);
  };

  const profileIcon = React.useMemo(
    () => ({
      user: UserIcon,
      settings: SettingsIcon,
    }),
    []
  );

  const handleViewTypeToggle = (viewType: string) => {
    setProfileSettings(prev => {
      const newViewTypes = prev.selectedViewTypes.includes(viewType)
        ? prev.selectedViewTypes.filter(v => v !== viewType)
        : [...prev.selectedViewTypes, viewType];
      
      return {
        ...prev,
        selectedViewTypes: newViewTypes,
        view: (newViewTypes[0] || prev.view) as 'column' | 'carousel' | 'single-doctor',
      };
    });
  };

  const handleLanguageChange = (language: string) => {
    setProfileSettings({
      ...profileSettings,
      language,
    });
  };

  const handleDoctorToggle = (doctorId: string) => {
    setProfileSettings(prev => {
      const normalizedId = String(doctorId).trim().toLowerCase();
      const isCurrentlySelected = prev.selectedDoctors.some(id => 
        String(id).trim().toLowerCase() === normalizedId
      );
      
      const newDoctors = isCurrentlySelected
        ? prev.selectedDoctors.filter(id => String(id).trim().toLowerCase() !== normalizedId)
        : [...prev.selectedDoctors, doctorId];
      
      return {
        ...prev,
        selectedDoctors: newDoctors,
      };
    });
  };

  const handleToggle = (field: 'token' | 'patient_name' | 'room_number' | 'slot' | 'booking_mode') => {
    if (localConfig) {
      userModifiedTVSettings.current = true;
      setLocalConfig({
        ...localConfig,
        [field]: !localConfig[field]
      });
    }
  };

  const navigateToProfileView = async (profileData: Profile) => {
    if (!hospital_id) return;

    if (profileData.language) {
      setAudioLanguageToLocalStorage(profileData.language);
    }

    if (profileData.doctors_list && Array.isArray(profileData.doctors_list) && profileData.doctors_list.length > 0) {
      let doctorsToMatch: Doctor[] = [];
      if (!allDoctorsData || allDoctorsData.length === 0) {
        const docResponse = await getDoctorListByHospitalId(hospital_id);
        if (docResponse?.status === 200 && docResponse?.data?.status === "SUCCESS") {
          doctorsToMatch = docResponse.data.result || [];
        }
      } else {
        doctorsToMatch = allDoctorsData;
      }

      if (doctorsToMatch.length > 0) {
        const doctorIds = profileData.doctors_list.map((doc: any) => {
          if (typeof doc === 'object' && doc !== null) {
            return doc.id || doc.doctor_id || doc.mapping_id || null;
          }
          return typeof doc === 'string' ? doc.trim() : null;
        }).filter(Boolean);

        const selectedDoctors = doctorsToMatch.filter((doc: Doctor) => {
          if (!doc) return false;
          
          return doctorIds.some((profileDoctorId: string) => {
            if (!profileDoctorId) return false;
            
            const normalizedProfileId = String(profileDoctorId).trim().toLowerCase();
            
            if (doc.mapping_id) {
              const normalizedMappingId = String(doc.mapping_id).trim().toLowerCase();
              if (normalizedMappingId === normalizedProfileId) return true;
            }
            
            if (doc.doctor_id) {
              const normalizedDoctorId = String(doc.doctor_id).trim().toLowerCase();
              if (normalizedDoctorId === normalizedProfileId) return true;
            }
            
            if ((doc as any).id) {
              const normalizedId = String((doc as any).id).trim().toLowerCase();
              if (normalizedId === normalizedProfileId) return true;
            }
            
            return false;
          });
        });

        if (selectedDoctors.length > 0) {
          setDoctors(selectedDoctors);
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    }

    const viewType = profileData.view_type;
    if (viewType === "single-doctor" || viewType === "carousel") {
      navigate(`/${hospital_id}/carousel-view`);
    } else if (viewType === "column") {
      navigate(`/${hospital_id}/column-carousel-view`);
    } else {
      navigate(`/${hospital_id}`);
    }
  };

  const handleSave = async () => {
    if (!hospital_id || !localConfig) return;

    const tvConfigSuccess = await updateConfiguration(hospital_id, {
      token: localConfig.token,
      patient_name: localConfig.patient_name,
      room_number: localConfig.room_number,
      slot: localConfig.slot,
      booking_mode: localConfig.booking_mode
    });

    let profileSuccess = true;
    if (currentProfile === defaultProfileId && accessToken) {
      try {
        const response = await updateProfileByHospitalAndProfileId(
          hospital_id,
          currentProfile,
          {
            language: profileSettings.language as "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr" | null,
            token: localConfig.token,
            patient_name: localConfig.patient_name,
            room_number: localConfig.room_number,
            slot: localConfig.slot,
            booking_mode: localConfig.booking_mode,
          }
        );

        if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
          profileSuccess = true;
          if (hospital_id) {
            saveProfileId(hospital_id, currentProfile);
          }
          if (currentProfileData) {
            await navigateToProfileView(currentProfileData);
            return;
          }
        } else {
          profileSuccess = false;
        }
      } catch (error) {
        console.error("Error saving default profile settings:", error);
        profileSuccess = false;
      }
    } else if (currentProfile !== defaultProfileId && accessToken) {
      try {
        const primaryViewType = profileSettings.selectedViewTypes[0] || profileSettings.view;
        
        const response = await updateProfileByHospitalAndProfileId(
          hospital_id,
          currentProfile,
          {
            view_type: primaryViewType as "column" | "carousel" | "single-doctor" | null,
            language: profileSettings.language as "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr" | null,
            doctors_list: profileSettings.selectedDoctors,
            token: localConfig.token,
            patient_name: localConfig.patient_name,
            room_number: localConfig.room_number,
            slot: localConfig.slot,
            booking_mode: localConfig.booking_mode,
          }
        );

        if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
          profileSuccess = true;
          if (hospital_id) {
            saveProfileId(hospital_id, currentProfile);
          }
          
          const updatedProfileData: Profile = {
            ...currentProfileData!,
            view_type: primaryViewType as "column" | "carousel" | "single-doctor",
            language: profileSettings.language as "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr",
            doctors_list: profileSettings.selectedDoctors.map(id => ({ id, name: '' })),
          };
          
          await navigateToProfileView(updatedProfileData);
          return;
        } else {
          profileSuccess = false;
        }
      } catch (error) {
        console.error("Error saving profile settings:", error);
        profileSuccess = false;
      }
    }

    if (tvConfigSuccess && profileSuccess) {
      if (error) {
        toast.info('Settings saved locally. API not available yet.');
      } else {
        toast.success('Settings saved successfully!');
      }
      
      if (currentProfile === defaultProfileId) {
        navigate(`/${hospital_id}`);
      } else if (currentProfileData) {
        await navigateToProfileView(currentProfileData);
      } else {
        navigate(`/${hospital_id}`);
      }
    } else {
      toast.error(error || 'Failed to save settings. Please try again.');
    }
  };

  const handleBack = async () => {
    if (!hospital_id) return;

    if (currentProfile === defaultProfileId) {
      navigate(`/${hospital_id}`);
      return;
    }

    if (currentProfileData) {
      await navigateToProfileView(currentProfileData);
    } else {
      navigate(`/${hospital_id}`);
    }
  };

  const FieldToggle = ({ 
    label, 
    field,
    isEnabled, 
    onToggle,
    isDisabled 
  }: { 
    label: string; 
    field: 'token' | 'patient_name' | 'room_number' | 'slot' | 'booking_mode';
    isEnabled: boolean; 
    isDisabled?: boolean;
    onToggle: () => void 
  }) => (
    <div className={`flex items-center justify-between p-3 border border-gray-200 rounded-lg ${
      isDisabled ? 'bg-gray-100' : 'hover:bg-gray-50' 
    }`}>
      <span className={`text-sm font-medium ${
        isDisabled ? 'text-gray-400' : 'text-gray-700' 
      }`}>{label}</span>
      <label className={`relative inline-flex items-center ${
        isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer' 
      }`}>
        <input
          type="checkbox"
          className="sr-only peer"
          checked={isEnabled}
          disabled={isDisabled}
          onChange={onToggle}
        />
        <div className={`group peer bg-white rounded-full duration-300 w-12 sm:w-14 h-5 sm:h-6 ring-[1.5px] ring-[#A6A6A6] after:duration-300 after:bg-[#A6A6A6] peer-checked:after:bg-blue-600 peer-checked:ring-blue-600 after:rounded-full after:absolute after:h-3 after:w-3 sm:after:h-4 sm:after:w-4 after:top-[3.5px] sm:after:top-[3.5px] after:left-1 after:flex after:justify-center after:items-center peer-checked:after:translate-x-7 sm:peer-checked:after:translate-x-8 peer-hover:after:scale-95 ${
          isEnabled ? 'bg-[#0F3CBE] after:translate-x-5' : 'bg-gray-200'
        } peer-disabled:after:bg-gray-300 peer-disabled:ring-gray-300`} /> 
      </label>
    </div>
  );

  if (loading || !localConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F3CBE] mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {!hospital_id ? 'Loading hospital information...' : 
             !accessToken ? 'Loading authentication...' : 
             'Loading settings...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center justify-between p-4">
        <div className="bg-gray-200 p-2 rounded-full m-4">
          <HiArrowLeft
            className="cursor-pointer w-5 h-5 font-semibold rounded-full"
            onClick={handleBack}
          />
        </div>
        <Logo />
      </div>

      {error && (
        <div className="w-full px-4 sm:px-6 mb-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                </svg>
                <span className="text-red-800">{error}</span>
              </div>
              <button
                onClick={clearError}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {error && error.includes('API not available') && (
        <div className="w-full px-4 sm:px-6 mb-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <span className="text-yellow-800">Settings will be saved locally. API endpoint not available yet.</span>
            </div>
          </div>
        </div>
      )}

      <div className=" mx-auto p-4 sm:p-6">
        <div className=" mx-6 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className={`flex items-center ${apiProfiles.length > 1 ? 'justify-between' : 'justify-end'} mb-6`}>
                {apiProfiles.length > 1 && (
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-[#0F3CBE] text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h1 className="text-lg font-semibold text-gray-800">User Profile</h1>
                      <p className="text-sm text-gray-500">Manage your profile settings</p>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`px-6 py-2 text-white rounded-lg font-semibold transition-colors shadow-md ${
                    saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#0F3CBE] hover:bg-blue-700'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

            <div className="space-y-6">
              {apiProfiles.length > 1 && (
                <div>
                  <h2 className="text-base font-semibold text-gray-800 mb-4">Select User Profile</h2>
                  <div className="flex flex-wrap items-center gap-4 md:gap-6">
                    {apiProfiles.map((profile: any, index: number) => {
                      const profileId = profile.id || profile.tv_config_profile_id || profile.profile_id;
                      const profileName = profile.tv_profile_name || profile.profile_name || `Profile ${index + 1}`;
                      const isDefault = profile.is_default === true;
                      const isActive = currentProfile === profileId;
                      const bgColor = index % 2 === 0 ? "#0B74FF" : "#053B7D";
                      
                      return (
                        <button
                          key={profileId}
                          className="flex flex-col items-center focus:outline-none"
                          onClick={() => handleProfileChange(profileId)}
                        >
                          <div
                            className="w-[100px] h-[100px] md:w-[120px] md:h-[120px] rounded-lg transition-all duration-200 flex items-center justify-center border-2"
                            style={{
                              backgroundColor: bgColor,
                              borderColor: isActive ? '#2668FF' : 'transparent',
                              boxShadow: isActive ? '0 5px 18px rgba(38,104,255,0.35)' : undefined,
                            }}
                          >
                            {React.createElement(profileIcon.user, {
                              color: "#FFFFFF",
                            })}
                          </div>
                          <p
                            className={`mt-3 text-sm md:text-base font-medium text-center ${
                              isActive ? 'text-[#0B74FF]' : 'text-[#1F2B4D]'
                            }`}
                          >
                            {isDefault ? "Default Profile" : profileName}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {currentProfile !== defaultProfileId && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                      </svg>
                      Select View Type
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {viewOptions.map((option) => {
                        const isSelected = profileSettings.selectedViewTypes.includes(option.value);
                        return (
                          <label
                            key={option.value}
                            className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                              isSelected
                                ? 'border-[#0F3CBE] bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleViewTypeToggle(option.value)}
                              className="w-4 h-4 text-[#0F3CBE] border-gray-300 rounded focus:ring-[#0F3CBE] focus:ring-2"
                            />
                            <span className={`ml-3 text-sm font-medium ${
                              isSelected ? 'text-[#0F3CBE]' : 'text-gray-700'
                            }`}>
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {profileSettings.selectedViewTypes.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">
                        Selected: {profileSettings.selectedViewTypes.map(v => 
                          viewOptions.find(o => o.value === v)?.label
                        ).join(', ')}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.242-.243-.47-.498-.68-.76a18.87 18.87 0 01-3.39 3.39 1 1 0 11-1.415-1.414c.243-.242.517-.47.76-.68a18.87 18.87 0 01-4.78-1.724V11a1 1 0 012 0v1.422a16.87 16.87 0 003.39-1.724c.243.243.517.47.76.68a1 1 0 11-1.415 1.414c-.243-.242-.47-.498-.68-.76a16.87 16.87 0 01-1.724-3.39H2a1 1 0 110-2h1.422a16.87 16.87 0 011.724-3.39c.21-.262.437-.517.68-.76a1 1 0 011.415 1.414c-.243.243-.47.498-.68.76a16.87 16.87 0 01-3.39 1.724V7a1 1 0 11-2 0V5.578a18.87 18.87 0 014.78-1.724c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.318-.33-.624-.672-.914-1.026A18.87 18.87 0 017 3V2a1 1 0 011-1zm5 4a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1V7a1 1 0 011-1z" clipRule="evenodd"/>
                      </svg>
                      Choose Audio Language
                    </label>
                    <select
                      value={profileSettings.language}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F3CBE] focus:border-[#0F3CBE] outline-none bg-white"
                    >
                      {languageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                      </svg>
                      Select Doctors
                    </label>
                    {doctorOptions.length === 0 ? (
                      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-500">No doctors available</p>
                      </div>
                    ) : (
                      <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                        {doctorOptions.map((doctor) => {
                          const doctorMappingId = doctor.mapping_id || '';
                          const doctorId = doctor.doctor_id || '';
                          const doctorName = doctor.full_name || 'Unknown Doctor';
                          
                          const isSelected = profileSettings.selectedDoctors.length > 0 && profileSettings.selectedDoctors.some((selectedId: string) => {
                            if (!selectedId) return false;
                            
                            if (doctorMappingId && doctorMappingId === selectedId) {
                              return true;
                            }
                            if (doctorId && doctorId === selectedId) {
                              return true;
                            }
                            
                            const normalizedSelected = String(selectedId).trim().toLowerCase();
                            const normalizedMappingId = String(doctorMappingId).trim().toLowerCase();
                            const normalizedDoctorId = String(doctorId).trim().toLowerCase();
                            
                            if (normalizedMappingId && normalizedMappingId === normalizedSelected) {
                              return true;
                            }
                            if (normalizedDoctorId && normalizedDoctorId === normalizedSelected) {
                              return true;
                            }
                            
                            return false;
                          });
                          
                          const doctorIdentifier = doctorMappingId || doctorId || '';
                          return (
                            <label
                              key={doctorIdentifier}
                              className={`flex items-center p-2 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'bg-blue-50 border border-[#0F3CBE]'
                                  : 'hover:bg-gray-50 border border-transparent'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleDoctorToggle(doctorMappingId || doctorId)}
                                className="w-4 h-4 text-[#0F3CBE] border-gray-300 rounded focus:ring-[#0F3CBE] focus:ring-2"
                              />
                              <span className={`ml-3 text-sm ${
                                isSelected ? 'text-[#0F3CBE] font-medium' : 'text-gray-700'
                              }`}>
                                {doctorName}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {profileSettings.selectedDoctors.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">
                        {profileSettings.selectedDoctors.length} doctor(s) selected
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="pt-6 border-t border-gray-100">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 rounded-full bg-[#0F3CBE] text-white">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 12a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2a1 1 0 011-1h10z"/>
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4z"/>
                    </svg>
                  </div>
                  <h1 className="text-lg font-semibold text-gray-800">TV Display Settings</h1>
                </div>

                <div className="space-y-3">
                  {(() => {
                    const isPatientNameDisabled = localConfig?.token === false;
                    const isTokenDisabled = localConfig?.patient_name === false;
                    return (
                      <>
                        <FieldToggle
                          label="Token Number"
                          field="token"
                          isEnabled={localConfig?.token || false}
                          onToggle={() => handleToggle('token')}
                          isDisabled={isTokenDisabled}
                        />
                        <FieldToggle
                          label="Patient Name"
                          field="patient_name"
                          isEnabled={localConfig?.patient_name || false}
                          onToggle={() => handleToggle('patient_name')}
                          isDisabled={isPatientNameDisabled}
                        />
                      </>
                    );
                  })()}
                  <FieldToggle
                    label="Slot Timing"
                    field="slot"
                    isEnabled={localConfig?.slot || false}
                    onToggle={() => handleToggle('slot')}
                  />
                  <FieldToggle
                    label="Room Number"
                    field="room_number"
                    isEnabled={localConfig?.room_number || false}
                    onToggle={() => handleToggle('room_number')}
                  />
                  <FieldToggle
                    label="Booking/Walk-in"
                    field="booking_mode"
                    isEnabled={localConfig?.booking_mode || false}
                    onToggle={() => handleToggle('booking_mode')}
                  />
                  {localConfig?.booking_mode && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                        </svg>
                        <span className="text-red-800 text-sm">This Walk-in/Booking column will not be visible in the column view</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8" />
      </div>
    </div>
  );
};

export default Settings;
